import { useCallback, useEffect, useState } from "react";

import { useSession } from "../../session/SessionContext.js";

/**
 * When support is open.
 *
 * The screen leads with whether support is open **right now**, before any of the
 * editing controls. That is the question an administrator actually opens this page
 * to answer, and it is also the fastest way to notice that a change did something
 * other than what was intended — a schedule that reads correctly and evaluates to
 * "closed" on a Tuesday afternoon is the failure this banner catches.
 *
 * Times are entered and shown in the schedule's own timezone, never the browser's.
 * An administrator in London editing Hawaii's hours must be typing Hawaii's clock,
 * or every window lands ten hours out.
 */

interface OpenWindow {
  start: string;
  end: string;
}

interface ScheduleException {
  date: string;
  label: string;
  windows: OpenWindow[];
}

type Weekly = Record<string, OpenWindow[]>;

interface Schedule {
  timezone: string;
  weekly: Weekly;
  exceptions: ScheduleException[];
  alwaysOpen: boolean;
}

interface Payload {
  schedule: Schedule;
  availability: { supportOpen: boolean; nextOpensAt: string | null };
}

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

/**
 * Zones offered in the picker.
 *
 * A short list of the ones this business plausibly runs on, plus whatever is already
 * stored — so a schedule set to a zone outside the list is never silently rewritten
 * by opening the page. Any IANA name is valid; the API validates against the runtime
 * rather than a fixed list, because zones get added and renamed.
 */
const COMMON_ZONES = [
  "Pacific/Honolulu",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "Europe/London",
  "UTC",
];

function label(day: string): string {
  return day.charAt(0).toUpperCase() + day.slice(1);
}

export function BusinessHours() {
  const { api } = useSession();

  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [availability, setAvailability] = useState<Payload["availability"] | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [holidayDate, setHolidayDate] = useState("");
  const [holidayLabel, setHolidayLabel] = useState("");

  const load = useCallback(async () => {
    try {
      const body = await api.get<Payload>("/support/business-hours");

      setSchedule(body.schedule);
      setAvailability(body.availability);
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(): Promise<void> {
    if (schedule === null) return;

    setSaving(true);
    setMessage(null);

    try {
      const body = await api.put<Payload>("/support/business-hours", schedule);

      // The response carries the freshly evaluated state, so the banner updates from
      // what the server computed rather than from what the browser assumes.
      setAvailability(body.availability);
      setMessage("Saved.");
    } catch {
      setMessage("That did not save. Check the times and try again.");
    } finally {
      setSaving(false);
    }
  }

  function setDay(day: string, windows: OpenWindow[]): void {
    setSchedule((current) =>
      current === null ? current : { ...current, weekly: { ...current.weekly, [day]: windows } },
    );
  }

  if (status === "loading") return <p className="muted">Loading…</p>;
  if (status === "error" || schedule === null) {
    return <p className="error">The schedule could not be loaded.</p>;
  }

  const zones = COMMON_ZONES.includes(schedule.timezone)
    ? COMMON_ZONES
    : [schedule.timezone, ...COMMON_ZONES];

  return (
    <div className="admin-form">
      <section className="card">
        <div className="row">
          <span className={availability?.supportOpen === true ? "tag live" : "tag"}>
            {availability?.supportOpen === true ? "Support is open now" : "Support is closed now"}
          </span>

          {availability?.supportOpen === false && availability.nextOpensAt !== null && (
            <span className="small muted">
              Opens again {new Date(availability.nextOpensAt).toLocaleString()}
            </span>
          )}
        </div>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={schedule.alwaysOpen}
            onChange={(event) => {
              setSchedule({ ...schedule, alwaysOpen: event.target.checked });
            }}
          />
          <span>
            Always available
            <span className="hint">
              Ignores the schedule entirely and treats support as open at all times. This is the
              default, so that a platform which has never configured hours does not quietly stop
              offering a human to anybody.
            </span>
          </span>
        </label>

        <label>
          Timezone
          <select
            value={schedule.timezone}
            onChange={(event) => {
              setSchedule({ ...schedule, timezone: event.target.value });
            }}
          >
            {zones.map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>
          <span className="hint">
            All times below are in this zone. Daylight saving is handled automatically — 09:00 stays
            09:00 for the people working it.
          </span>
        </label>
      </section>

      <section className="card" aria-disabled={schedule.alwaysOpen}>
        <h2>Weekly hours</h2>
        {schedule.alwaysOpen && (
          <p className="hint">
            Not in use while &ldquo;always available&rdquo; is on. Edits are kept.
          </p>
        )}

        <table className="grid">
          <tbody>
            {DAYS.map((day) => {
              const windows = schedule.weekly[day] ?? [];

              return (
                <tr key={day}>
                  <td style={{ width: 110 }}>
                    <strong>{label(day)}</strong>
                  </td>
                  <td>
                    {windows.length === 0 && <span className="small muted">Closed</span>}

                    {windows.map((window, index) => (
                      <span key={index} className="row" style={{ gap: 6, marginBottom: 4 }}>
                        <input
                          type="time"
                          value={window.start}
                          onChange={(event) => {
                            setDay(
                              day,
                              windows.map((existing, position) =>
                                position === index
                                  ? { ...existing, start: event.target.value }
                                  : existing,
                              ),
                            );
                          }}
                        />
                        <span className="small muted">to</span>
                        <input
                          type="time"
                          value={window.end}
                          onChange={(event) => {
                            setDay(
                              day,
                              windows.map((existing, position) =>
                                position === index
                                  ? { ...existing, end: event.target.value }
                                  : existing,
                              ),
                            );
                          }}
                        />
                        <button
                          type="button"
                          aria-label={`Remove ${label(day)} window`}
                          onClick={() => {
                            setDay(
                              day,
                              windows.filter((_, position) => position !== index),
                            );
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </td>
                  <td className="right">
                    <button
                      type="button"
                      disabled={windows.length >= 4}
                      onClick={() => {
                        setDay(day, [...windows, { start: "09:00", end: "17:00" }]);
                      }}
                    >
                      Add hours
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <p className="hint">
          Several windows a day are allowed, for a lunch break. Overnight cover needs a window on
          each of the two days rather than one that wraps past midnight.
        </p>
      </section>

      <section className="card">
        <h2>Holidays and one-off changes</h2>
        <p className="hint">
          A date here replaces that day&apos;s weekly hours entirely — with no hours it is closed,
          and with hours it is a half day or extra cover.
        </p>

        <table className="grid">
          <tbody>
            {schedule.exceptions.map((exception, index) => (
              <tr key={exception.date}>
                <td>
                  <strong>{exception.date}</strong>
                  <div className="small muted">
                    {exception.label === "" ? "—" : exception.label} ·{" "}
                    {exception.windows.length === 0
                      ? "closed all day"
                      : exception.windows.map((w) => `${w.start}–${w.end}`).join(", ")}
                  </div>
                </td>
                <td className="right">
                  <button
                    type="button"
                    onClick={() => {
                      setSchedule({
                        ...schedule,
                        exceptions: schedule.exceptions.filter((_, position) => position !== index),
                      });
                    }}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="row">
          <input
            type="date"
            value={holidayDate}
            onChange={(event) => {
              setHolidayDate(event.target.value);
            }}
          />
          <input
            type="text"
            placeholder="Christmas Day"
            value={holidayLabel}
            maxLength={120}
            onChange={(event) => {
              setHolidayLabel(event.target.value);
            }}
          />
          <button
            type="button"
            disabled={
              holidayDate === "" ||
              schedule.exceptions.some((exception) => exception.date === holidayDate)
            }
            onClick={() => {
              setSchedule({
                ...schedule,
                exceptions: [
                  ...schedule.exceptions,
                  { date: holidayDate, label: holidayLabel, windows: [] },
                ].sort((left, right) => left.date.localeCompare(right.date)),
              });
              setHolidayDate("");
              setHolidayLabel("");
            }}
          >
            Add closed day
          </button>
        </div>
      </section>

      <div className="row sticky-actions">
        <button className="primary" disabled={saving} onClick={() => void save()}>
          {saving ? "Saving…" : "Save schedule"}
        </button>
        <button disabled={saving} onClick={() => void load()}>
          Discard
        </button>
        {message !== null && <span className="small muted">{message}</span>}
      </div>
    </div>
  );
}
